import React, { useEffect, useState } from 'react';
import { createEvent, deleteEvent, listEventRegistrations, getEventById, updateEvent } from '../../utils/events';
import { collection, getDocs, orderBy, query, limit, startAfter } from 'firebase/firestore';
import { db } from '../../firebase';
import { cldConfig } from '../../config/cloudinary';

function useEventsAdminList() {
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
    const [cursor, setCursor] = useState(null);
    const pageSize = 20;
    async function refresh() {
		setError('');
		try {
            const qy = query(collection(db, 'events'), orderBy('startAt', 'desc'), limit(pageSize));
            const res = await getDocs(qy);
            setItems(res.docs.map(d => ({ id: d.id, ...d.data() })));
            setCursor(res.docs.length > 0 ? res.docs[res.docs.length - 1] : null);
		} catch (e) {
			setError('Failed to load events');
		} finally {
			setLoading(false);
		}
	}
    async function loadMore() {
        if (!cursor) return;
        const qy = query(collection(db, 'events'), orderBy('startAt', 'desc'), startAfter(cursor), limit(pageSize));
        const res = await getDocs(qy);
        setItems(prev => [...prev, ...res.docs.map(d => ({ id: d.id, ...d.data() }))]);
        setCursor(res.docs.length > 0 ? res.docs[res.docs.length - 1] : null);
    }
	useEffect(() => { refresh(); }, []);
    return { items, loading, error, refresh, loadMore, hasMore: Boolean(cursor) };
}

const emptyForm = {
	title: '', description: '', location: '', isOnline: false,
	registrationUrl: '', bannerImageUrl: '', capacity: '', tags: '',
	startAt: '', endAt: ''
};

function EventForm({ onCancel, onSaved, initial }) {
    const [form, setForm] = useState(() => {
        if (!initial) return emptyForm;
        const normalized = {
            ...emptyForm,
            ...initial,
            title: initial.title ?? '',
            description: initial.description ?? '',
            location: initial.location ?? '',
            isOnline: Boolean(initial.isOnline),
            registrationUrl: initial.registrationUrl ?? '',
            bannerImageUrl: initial.bannerImageUrl ?? '',
            capacity: initial.capacity == null ? '' : String(initial.capacity),
            tags: Array.isArray(initial.tags) ? initial.tags.join(', ') : (initial.tags ?? ''),
            startAt: initial.startAt?.toDate ? initial.startAt.toDate().toISOString().slice(0,16) : '',
            endAt: initial.endAt?.toDate ? initial.endAt.toDate().toISOString().slice(0,16) : ''
        };
        return normalized;
    });
	const [saving, setSaving] = useState(false);
	const [uploading, setUploading] = useState(false);

	const handleChange = (e) => {
		const { name, value, type, checked } = e.target;
		setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
	};

	const uploadBanner = async (file) => {
		if (!file) return;
		setUploading(true);
		try {
			const formData = new FormData();
			formData.append('file', file);
			formData.append('upload_preset', cldConfig.uploadPreset);
			formData.append('folder', 'events');
			const resp = await fetch(`https://api.cloudinary.com/v1_1/${cldConfig.cloudName}/image/upload`, { method: 'POST', body: formData });
			if (!resp.ok) throw new Error('Upload failed');
			const data = await resp.json();
			setForm(prev => ({ ...prev, bannerImageUrl: data.secure_url }));
		} catch (e) {
			alert('Failed to upload image');
		} finally {
			setUploading(false);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setSaving(true);
		try {
			const payload = {
				title: form.title,
				description: form.description,
				location: form.location,
				isOnline: Boolean(form.isOnline),
				registrationUrl: form.registrationUrl || null,
				bannerImageUrl: form.bannerImageUrl || null,
				capacity: form.capacity ? Number(form.capacity) : null,
				tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
				startAt: new Date(form.startAt),
				endAt: new Date(form.endAt)
			};
			if (initial?.id) {
				await updateEvent(initial.id, payload);
			} else {
				await createEvent(payload);
			}
			onSaved();
		} catch (e) {
			alert('Failed to save event');
		} finally {
			setSaving(false);
		}
	};

	return (
		<form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
			<div>
				<label className="block text-sm font-medium">Title</label>
				<input name="title" value={form.title} onChange={handleChange} className="mt-1 w-full border rounded-md px-3 py-2" required />
			</div>
			<div>
				<label className="block text-sm font-medium">Location</label>
				<input name="location" value={form.location} onChange={handleChange} className="mt-1 w-full border rounded-md px-3 py-2" disabled={form.isOnline} />
			</div>
			<div>
				<label className="block text-sm font-medium">Start</label>
				<input type="datetime-local" name="startAt" value={form.startAt} onChange={handleChange} className="mt-1 w-full border rounded-md px-3 py-2" required />
			</div>
			<div>
				<label className="block text-sm font-medium">End</label>
				<input type="datetime-local" name="endAt" value={form.endAt} onChange={handleChange} className="mt-1 w-full border rounded-md px-3 py-2" required />
			</div>
			<div className="md:col-span-2">
				<label className="inline-flex items-center gap-2 text-sm font-medium">
					<input type="checkbox" name="isOnline" checked={form.isOnline} onChange={handleChange} />
					Online event
				</label>
			</div>
			<div className="md:col-span-2">
				<label className="block text-sm font-medium">External registration URL (optional)</label>
				<input name="registrationUrl" value={form.registrationUrl} onChange={handleChange} className="mt-1 w-full border rounded-md px-3 py-2" placeholder="https://..." />
			</div>
			<div className="md:col-span-2">
				<label className="block text-sm font-medium">Description</label>
				<textarea name="description" rows="6" value={form.description} onChange={handleChange} className="mt-1 w-full border rounded-md px-3 py-2" />
			</div>
			<div>
				<label className="block text-sm font-medium">Capacity (optional)</label>
				<input type="number" name="capacity" value={form.capacity} onChange={handleChange} className="mt-1 w-full border rounded-md px-3 py-2" />
			</div>
			<div>
				<label className="block text-sm font-medium">Tags (comma separated)</label>
				<input name="tags" value={form.tags} onChange={handleChange} className="mt-1 w-full border rounded-md px-3 py-2" placeholder="webinar, workshop" />
			</div>
			<div className="md:col-span-2">
				<label className="block text-sm font-medium">Banner image</label>
				<div className="mt-2 flex items-center gap-3">
					<input type="file" accept="image/*" onChange={(e) => uploadBanner(e.target.files?.[0])} />
					{uploading && <span className="text-sm text-gray-600">Uploading...</span>}
					{form.bannerImageUrl && <img src={form.bannerImageUrl} alt="banner" className="h-14 rounded" />}
				</div>
			</div>
			<div className="md:col-span-2 flex gap-3 justify-end">
				<button type="button" onClick={onCancel} className="px-4 py-2 border rounded-md">Cancel</button>
				<button type="submit" disabled={saving} className="px-4 py-2 rounded-md text-white bg-[rgb(130,88,18)] disabled:opacity-60">{saving ? 'Saving...' : 'Save event'}</button>
			</div>
		</form>
	);
}

function RegistrationsList({ eventId, onClose }) {
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(true);
	useEffect(() => {
		let mounted = true;
		(async () => {
			const list = await listEventRegistrations(eventId);
			if (mounted) { setRows(list); setLoading(false); }
		})();
		return () => { mounted = false; };
	}, [eventId]);
	return (
		<div>
			<div className="flex items-center justify-between mb-3">
				<h3 className="text-lg font-bold">Registrations</h3>
				<button onClick={onClose} className="text-sm text-gray-600">Close</button>
			</div>
			{loading ? <p>Loading...</p> : (
				<table className="w-full text-sm">
					<thead>
						<tr className="text-left text-gray-500">
							<th className="pb-2 pr-4">Name</th>
							<th className="pb-2 pr-4">Email</th>
							<th className="pb-2">Phone</th>
						</tr>
					</thead>
					<tbody>
						{rows.map(r => (
							<tr key={r.id} className="border-t">
								<td className="py-2 pr-4">{r.name}</td>
								<td className="py-2 pr-4">{r.email}</td>
								<td className="py-2">{r.phone}</td>
							</tr>
						))}
					</tbody>
				</table>
			)}
		</div>
	);
}

function EventManagement() {
    const { items, loading, error, refresh, loadMore, hasMore } = useEventsAdminList();
	const [creating, setCreating] = useState(false);
	const [editingId, setEditingId] = useState('');
	const [viewingRegsFor, setViewingRegsFor] = useState('');

	return (
		<div>
			<div className="flex items-center justify-between mb-6">
				<h1 className="text-2xl font-bold">Event Management</h1>
				<button onClick={() => { setCreating(true); setEditingId(''); }} className="px-4 py-2 rounded-md bg-[rgb(130,88,18)] text-white">Create event</button>
			</div>

			{creating && (
				<div className="mb-8 border rounded-lg p-4 bg-white shadow-sm">
					<h2 className="text-lg font-semibold mb-3">New event</h2>
					<EventForm onCancel={() => setCreating(false)} onSaved={() => { setCreating(false); refresh(); }} />
				</div>
			)}

			{editingId && (
				<div className="mb-8 border rounded-lg p-4 bg-white shadow-sm">
					<h2 className="text-lg font-semibold mb-3">Edit event</h2>
					<EditEvent eventId={editingId} onCancel={() => setEditingId('')} onSaved={() => { setEditingId(''); refresh(); }} />
				</div>
			)}

			{viewingRegsFor && (
				<div className="mb-8 border rounded-lg p-4 bg-white shadow-sm">
					<RegistrationsList eventId={viewingRegsFor} onClose={() => setViewingRegsFor('')} />
				</div>
			)}

            {loading ? <p>Loading...</p> : error ? <p className="text-red-600">{error}</p> : (
				<table className="w-full">
					<thead>
						<tr className="text-left text-sm text-gray-500">
							<th className="pb-2 pr-4">Start</th>
							<th className="pb-2 pr-4">Title</th>
							<th className="pb-2 pr-4">Where</th>
							<th className="pb-2">Actions</th>
						</tr>
					</thead>
					<tbody>
						{items.map(ev => {
							const start = ev.startAt?.toDate ? ev.startAt.toDate() : new Date(ev.startAt);
							return (
								<tr key={ev.id} className="border-t">
									<td className="py-2 pr-4 text-sm text-gray-700 whitespace-nowrap">{start.toLocaleString()}</td>
									<td className="py-2 pr-4">{ev.title}</td>
									<td className="py-2 pr-4 text-sm text-gray-700">{ev.isOnline ? 'Online' : ev.location}</td>
									<td className="py-2 text-sm">
										<div className="flex gap-2">
											<button onClick={() => setViewingRegsFor(ev.id)} className="px-3 py-1 border rounded-md">Registrations</button>
											<button onClick={() => setEditingId(ev.id)} className="px-3 py-1 border rounded-md">Edit</button>
											<button onClick={async () => { if (window.confirm('Delete this event?')) { await deleteEvent(ev.id); refresh(); } }} className="px-3 py-1 border rounded-md text-red-700">Delete</button>
										</div>
									</td>
								</tr>
							);
						})}
					</tbody>
                </table>
			)}
            {!loading && !error && hasMore && (
                <div className="mt-6 text-center">
                    <button onClick={loadMore} className="px-4 py-2 bg-[rgb(130,88,18)] text-white rounded-md hover:opacity-90">Load more</button>
                </div>
            )}
		</div>
	);
}

function EditEvent({ eventId, onCancel, onSaved }) {
	const [initial, setInitial] = useState(null);
	useEffect(() => {
		let mounted = true;
		(async () => {
			const ev = await getEventById(eventId);
			if (mounted) setInitial(ev);
		})();
		return () => { mounted = false; };
	}, [eventId]);
	if (!initial) return <p>Loading...</p>;
	return (
		<EventForm initial={initial} onCancel={onCancel} onSaved={onSaved} />
	);
}

export default EventManagement;
